import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { RoleLite } from '@blauturquesa/shared/roles-lite';
import { BottomSheet } from '../components/BottomSheet';
import { Icon } from '../components/Icon';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { useAuth } from '../features/auth/AuthProvider';
import { reloadApp, signOutCompletely, switchProfile } from '../features/auth/actions';

/** Menú de l'avatar (§10.4): canvi de perfil, compte, idioma i tancar sessió. */
export function ProfileSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('shell');
  const state = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState<RoleLite | null>(null);
  const [error, setError] = useState(false);

  function go(path: string) {
    onClose();
    navigate(path);
  }

  async function choose(role: RoleLite) {
    setSwitching(role);
    setError(false);
    try {
      await switchProfile(role);
    } catch {
      setError(true);
      setSwitching(null);
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} label={t('sheet.label')}>
      {state.status === 'signedIn' ? (
        <>
          <div className="sheet-head">
            <Avatar name={state.user.displayName ?? state.user.email ?? ''} />
            <div>
              <strong>{state.user.displayName || state.user.email}</strong>
              {state.user.displayName && <div className="muted small">{state.user.email}</div>}
            </div>
          </div>

          <p className="sheet-section">{t('sheet.profiles')}</p>
          <div className="profile-list" role="radiogroup" aria-label={t('sheet.profiles')}>
            {state.roles.map((r) => {
              const active = r === state.activeRole;
              return (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  aria-label={`${t(`roles.${r}`)}. ${t(`roles.${r}Hint`)}`}
                  className={`profile-option${active ? ' is-active' : ''}`}
                  disabled={switching !== null}
                  onClick={() => (active ? onClose() : void choose(r))}
                >
                  <span className={`role-dot role-${r}`} />
                  <span className="grow">
                    {t(`roles.${r}`)}
                    <span className="muted small block">{t(`roles.${r}Hint`)}</span>
                  </span>
                  {active && <Icon name="check" size={20} />}
                  {switching === r && <span className="muted small">{t('sheet.switching')}</span>}
                </button>
              );
            })}
          </div>
          {error && <p className="error" role="alert">{t('sheet.switchError')}</p>}

          {(state.activeRole === 'agent' || state.activeRole === 'superadmin') && (
            <>
              {/* El que hi havia a la pestanya «Més» (§10.4), sempre a l'abast des de la capçalera. */}
              <p className="sheet-section">{t('more.admin')}</p>
              <div className="sheet-links">
                {state.activeRole === 'superadmin' && (
                  <button type="button" className="sheet-link" onClick={() => go('/mes/usuaris')}>
                    <Icon name="users" size={20} /> <span className="grow">{t('more.users')}</span> <Icon name="chevronRight" size={18} />
                  </button>
                )}
                {state.activeRole === 'superadmin' && (
                  <button type="button" className="sheet-link" onClick={() => go('/mes/skins')}>
                    <Icon name="image" size={20} /> <span className="grow">{t('more.skins')}</span> <Icon name="chevronRight" size={18} />
                  </button>
                )}
                <button type="button" className="sheet-link" onClick={() => go('/mes')}>
                  <Icon name="dots" size={20} /> <span className="grow">{t('more.all')}</span> <Icon name="chevronRight" size={18} />
                </button>
              </div>
            </>
          )}

          <div className="sheet-links">
            <button type="button" className="sheet-link" onClick={() => go('/compte')}>
              <Icon name="user" size={20} /> <span className="grow">{t('sheet.account')}</span> <Icon name="chevronRight" size={18} />
            </button>
            <div className="sheet-link">
              <Icon name="globe" size={20} /> <span className="grow">{t('sheet.language')}</span> <LocaleSwitcher />
            </div>
            <button type="button" className="sheet-link" onClick={reloadApp}>
              <Icon name="swap" size={20} /> <span className="grow">{t('sheet.reload')}<span className="muted small block">{t('sheet.reloadHint')}</span></span>
            </button>
            <button type="button" className="sheet-link" onClick={() => void signOutCompletely()}>
              <Icon name="logout" size={20} /> <span className="grow">{t('sheet.signOut')}</span>
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="sheet-section">{t('sheet.guest')}</p>
          <button type="button" className="btn-shiny wide" onClick={() => go('/entrar')}>{t('tabs.signIn')}</button>
          <div className="sheet-links">
            <div className="sheet-link">
              <Icon name="globe" size={20} /> <span className="grow">{t('sheet.language')}</span> <LocaleSwitcher />
            </div>
            <button type="button" className="sheet-link" onClick={reloadApp}>
              <Icon name="swap" size={20} /> <span className="grow">{t('sheet.reload')}<span className="muted small block">{t('sheet.reloadHint')}</span></span>
            </button>
          </div>
        </>
      )}
    </BottomSheet>
  );
}

export function Avatar({ name, small }: { name: string; small?: boolean }) {
  const initials = name
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  return <span className={`avatar${small ? ' small' : ''}`} aria-hidden="true">{initials || '·'}</span>;
}
